import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useIntl } from 'umi';
import { useDeepCompareEffect } from 'ahooks';
import { Button, Checkbox, Input, Justify, Radio, DatePicker } from 'tea-component';
import { FILE_TYPES_MAP } from '@gp/constants/const';
import SvgIcon from '@home/components/SvgIcon';
import GeneralModal from '@gp/components/GeneralModal';
import { UseFormSearch } from '@gp/pages/Pipeline/ProjectDetails/components/GameOverviewEdit/Components/UseFormSearch';
import { exportData } from '../../utils/exportExcelandCsv';
import { getCascadeProjectDatas, getCascadeProjectValues } from '@gp/utils/project';
import {
  flattenMetricsIndev,
  METRIC_NAME_LIST,
  downloadColumnsFormData,
  generateExtendedColumns,
  assembleInDevDownloadData,
} from '../../utils';
import { InDevProjectsTab } from '@/pages/strategic/utils/buriedPoint';
import CascadeProjectCustom from '@sg/components/CascadeProjectCustom';
import DatePickerWrapper from '@gp/components/DatePickerWrapper';
import { createBtnPoint, SGBuriedBtnPoint } from '@sg/utils/buriedPoint';
import './index.scss';

const Filter: React.FC<any> = (props: any) => {
  const { formatMessage } = useIntl();
  const {
    selectedDate,
    selectedTitle,
    selectYM,
    handleYMChange,
    isAggregate,
    exportFetchParams,
    selectedMonth,
    setSelectedMonth,
    cascadeValue,
    setCascadeValue,
    changeFilterProjects,
    loading,
    cascadeInfoList,
    cascadeTreeOptions,
    handleDateFilterChange,
    projects,
  } = props;

  const [visible, setVisible] = useState(false);

  const { MonthPicker } = DatePicker;

  const handleCascadeChange = (values: string[]) => {
    changeFilterProjects(values);
    setCascadeValue(values);
  };

  const handleDateChange = (value: any) => {
    setSelectedMonth(value);
    handleDateFilterChange?.(value);
  };

  // 计算所有最后节点的 project_id，修改getCascadeProjectValues方法
  const allCascadeValues = useMemo(() => {
    return getCascadeProjectValues(cascadeTreeOptions, false);
  }, [cascadeTreeOptions]);

  useDeepCompareEffect(() => {
    setCascadeValue(allCascadeValues);
  }, [allCascadeValues]);

  return (
    <>
      <Justify
        style={{
          marginBottom: '20px',
        }}
        top
        left={
          <section className="dbs-studio-monthly-report-download-title-card-filter">
            <CascadeProjectCustom
              options={cascadeTreeOptions}
              value={cascadeValue}
              onChange={handleCascadeChange}
              allValues={allCascadeValues}
              btnTitle="Studio/Project"
              disabled={loading}
              cascadeInfoList={cascadeInfoList}
            />
            <DatePickerWrapper className="dbs-month-picker-with-tooltip" label={formatMessage({ id: 'dbs_month' })}>
              <MonthPicker
                overlayClassName="dbs-month-picker-overlay"
                value={selectedMonth}
                onChange={handleDateChange}
                placeholder="Please Select"
                disabled={loading}
              />
            </DatePickerWrapper>
            <Checkbox.Group
              className="dbs-performance-card-filter-ym-selector"
              value={selectYM}
              onChange={handleYMChange}
            >
              <Checkbox name="2">MoM</Checkbox>
            </Checkbox.Group>

            {visible && (
              <LiveFilterDownloadModal
                {...{
                  visible,
                  close: () => setVisible(false),
                  selectedDate,
                  selectedTitle,
                  isAggregate,
                  exportFetchParams,
                  selectYM,
                  selectedMonth,
                  projects, // 使用原始项目数据进行导出
                }}
              />
            )}
          </section>
        }
        right={
          <Button
            type="primary"
            className="dbs-gradient-btn"
            onClick={() => {
              // Track buried point for Download button
              const btnPoint = createBtnPoint(InDevProjectsTab, 'download', 'Download');
              if (btnPoint) {
                SGBuriedBtnPoint(btnPoint);
              }
              setVisible(true);
            }}
          >
            <SvgIcon type="download" style={{ position: 'relative', left: '-4px' }} />
            {formatMessage({ id: 'dbs_download' })}
          </Button>
        }
      />
    </>
  );
};

export default React.memo(Filter);

interface ILiveFilterDownloadModalProps {
  visible: boolean;
  close: () => void;
  selectedDate: any[];
  selectedTitle: string[];
  isAggregate: boolean;
  exportFetchParams: Record<string, any>;
  selectYM: string[];
  selectedMonth?: any; // 当前选中的月份，用于计算 duration
  records?: any[]; // 宽表扁平化数据（用于表格展示）
  projects?: any[]; // 原始项目数据（用于下载导出）
}

export const LiveFilterDownloadModal = ({
  visible,
  close,
  selectedTitle,
  selectYM,
  selectedMonth,
  projects,
}: ILiveFilterDownloadModalProps) => {

  const durationTitle = useMemo(() => {
    const isSingle = !selectedTitle?.length || selectedTitle.length > 1;
    const monthMoment = moment(selectedMonth);
    if (!monthMoment.isValid()) {
      return '';
    }
    const start = monthMoment.clone().startOf('year').format('YYYY-MM');
    const end = monthMoment.format('YYYY-MM');
    return isSingle ? `In dev projects ${start} ~ ${end}` : `In dev projects ${selectedTitle?.[0] || ''} ${start} ~ ${end}`;
  }, [selectedMonth, selectedTitle]);

  const getFormValue = async (value: { fileFormat: string; duration: string }) => {
    const columns = downloadColumnsFormData(METRIC_NAME_LIST);
    const excelColumns = generateExtendedColumns({ columns, selectYM });

    const { fileFormat, duration } = value;
    try {
      // 直接使用原始项目数据进行扁平化处理，不再调用接口
      const rawProjects = projects || [];
      const flatData = flattenMetricsIndev(rawProjects);
      const excelDataSource = assembleInDevDownloadData(flatData, excelColumns);

      exportData({
        fileName: durationTitle,
        columns: excelColumns,
        data: excelDataSource,
        fileType: fileFormat,
      });
      close?.();
    } catch (error) {
      console.error('[InDev] [Download] 导出失败:', error);
    }
  };

  // duration 的取值要根据selectedMonth的值，显示从selectedMonth的当年的第一个月到selectedMonth选中的月份
  const duration = useMemo(() => {
    const isSingle = !selectedTitle?.length || selectedTitle.length > 1;
    const monthMoment = moment(selectedMonth);
    if (!monthMoment.isValid()) {
      return '';
    }
    const start = monthMoment.clone().startOf('year').format('YYYY-MM');
    const end = monthMoment.format('YYYY-MM');
    return isSingle ? `${start} ~ ${end}` : `${selectedTitle?.[0] || ''} ${start} ~ ${end}`;
  }, [selectedMonth, selectedTitle]);

  const SearchComponent = UseFormSearch({
    config: [
      {
        name: 'duration',
        label: 'Duration',
        component: <Input disabled className="gp-live-title-card-filter-download-container-input" />,
        rules: { message: '', required: false },
      },
      {
        name: 'fileFormat',
        label: 'File Format',
        component: (
          <Radio.Group>
            <Radio className="dbs-common-radio" name={FILE_TYPES_MAP.EXCEL}>
              Excel
            </Radio>
            <Radio className="dbs-common-radio" name={FILE_TYPES_MAP.CSV}>
              CSV
            </Radio>
          </Radio.Group>
        ),
        rules: { message: '', required: false },
      },
    ],
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    getFormValue: (value: any) => getFormValue(value as { fileFormat: string; duration: string }),
    labelSpan: 6,
    reset: visible,
    initialValues: {
      duration,
      fileFormat: '1',
    },
    btnCancelText: 'Cancel',
    isDisabledSearchBtn: true,
    resetCallback: () => {
      close?.();
    },
  });
  return (
    <GeneralModal
      title={'Download'}
      className="dbs-live-download-modal"
      modalSize="800"
      isFont14={true}
      isFooter={false}
      visible={visible}
      onClose={close}
      content={<div className="gp-live-title-card-filter-download-container font14">{SearchComponent}</div>}
    />
  );
};
